/**
 * Le connecteur Dropbox du mode `?demo` (ARTO-135) : en mémoire, sans réseau,
 * une connexion par dossier d'ouverture. Les dossiers de démonstration qui ont
 * une galerie montrent chacun un moment du parcours :
 *
 * - OUV-2026-003 : aucune connexion (le parcours complet se joue ici : tout
 *   code collé est accepté, l'import avance puis se termine) ;
 * - OUV-2026-004 : autorisation demandée, en attente du code ;
 * - OUV-2026-005 : autorisée, un import tourne (ses compteurs avancent en
 *   direct, il se termine en une minute environ) ;
 * - OUV-2026-006 : autorisée, import terminé, son dépôt est inventorié ;
 * - OUV-2026-007 : accès révoqué.
 *
 * `?connecteur=absent` dans l'adresse montre la plateforme sans clé Dropbox ;
 * `?connecteur=revocation-echec`, une révocation que Dropbox n'a pas confirmée.
 * Aucune galerie ni personne réelle : un compte et des dossiers inventés.
 */
import { ApiError } from './api.js';
import type {
  ConnexionDropbox,
  DossierDropbox,
  ImportConnexion,
  LancementImport,
  ListeDossiersDropbox,
  ReponseAutorisation,
  ReponseLancement,
  ReponseRevocation,
} from './connecteurs.js';
import { compteDemoCourant } from './demoData.js';
import { demoDepots } from './demoDepots.js';
import { demoOuvertures } from './demoOuvertures.js';

const MINUTE = 60_000;
const GIO = 1024 ** 3;
const DUREE_IMPORT_MS = 55_000;
const COMPTE = 'dbid:AAE-demonstration';

const DOSSIERS: DossierDropbox[] = [
  { chemin: '/Galerie', nom: 'Galerie', parent: null },
  { chemin: '/Galerie/Œuvres', nom: 'Œuvres', parent: '/Galerie' },
  { chemin: '/Galerie/Certificats', nom: 'Certificats', parent: '/Galerie' },
  { chemin: '/Galerie/Contrats d’artistes', nom: 'Contrats d’artistes', parent: '/Galerie' },
  { chemin: '/Photographies de l’atelier', nom: 'Photographies de l’atelier', parent: null },
  {
    chemin: '/Photographies de l’atelier/2024',
    nom: '2024',
    parent: '/Photographies de l’atelier',
  },
  {
    chemin: '/Photographies de l’atelier/2025',
    nom: '2025',
    parent: '/Photographies de l’atelier',
  },
  { chemin: '/Expositions', nom: 'Expositions', parent: null },
  { chemin: '/Expositions/Catalogues', nom: 'Catalogues', parent: '/Expositions' },
  { chemin: '/Expositions/Cartels', nom: 'Cartels', parent: '/Expositions' },
  { chemin: '/Comptabilité', nom: 'Comptabilité', parent: null },
  {
    chemin: '/Comptabilité/Factures 2019-2025',
    nom: 'Factures 2019-2025',
    parent: '/Comptabilité',
  },
  { chemin: '/Archives', nom: 'Archives', parent: null },
];

/** Ce que l'import verse : 210 fichiers, dont des images, des certificats et un carnet. */
const FICHIERS: (readonly [string, number])[] = [
  ...Array.from({ length: 168 }, (_, rang): readonly [string, number] => [
    `Galerie/Œuvres/oeuvre-${String(rang + 1).padStart(3, '0')}.${rang % 7 === 0 ? 'png' : 'jpg'}`,
    2_400_000 + ((rang * 937_211) % 6_800_000),
  ]),
  ...Array.from({ length: 36 }, (_, rang): readonly [string, number] => [
    `Galerie/Certificats/certificat-${String(rang + 1).padStart(3, '0')}.pdf`,
    180_000 + ((rang * 53_117) % 420_000),
  ]),
  ['Galerie/Contrats d’artistes/contrat-modele.pdf', 312_000],
  ['Galerie/Contrats d’artistes/contrat-depot-vente.pdf', 288_000],
  ['Galerie/Contacts-vip.csv', 41_000],
  ['Galerie/Inventaire-2025.xlsx', 914_000],
  ['Galerie/Notes.txt', 3_200],
  ['Galerie/Plan-accrochage.pdf', 1_240_000],
];
const OCTETS_TOTAUX = FICHIERS.reduce((somme, [, octets]) => somme + octets, 0);
const DEJA_PRESENTS = 9;
const ECARTES = 2;

interface Lancement {
  debut: number;
  duree: number;
  depotId: string;
  fini: boolean;
}

interface EtatDemo {
  connexion: ConnexionDropbox;
  lancement: Lancement | null;
}

const etats = new Map<string, EtatDemo>();
let compteur = 0;

const refus = (statut: number, code: string, raison: string, message: string): ApiError =>
  new ApiError(statut, code, message, { raison });

function variante(): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('connecteur');
}

const absent = (): boolean => variante() === 'absent';

function connexionVierge(): ConnexionDropbox {
  return {
    application: 'dropbox',
    disponible: true,
    mode: 'simule',
    etat: 'aucune',
    compte: null,
    autoriseeLe: null,
    autoriseePar: null,
    autorisationExpireLe: null,
    source: null,
    import: null,
    revoqueLe: null,
    purgeLe: null,
    revision: 0,
  };
}

/** Le dossier d'ouverture (pour sa galerie) ; le rang vient de son identifiant. */
function dossierDe(dossierId: string): { galerieId: string | null; rang: number } {
  const dossier = demoOuvertures.obtenir(dossierId);
  return { galerieId: dossier.galleryId, rang: Number(dossierId.slice(-4)) };
}

function importDe(
  etat: ImportConnexion['etat'],
  depotId: string,
  sur: Partial<ImportConnexion> = {},
): ImportConnexion {
  return {
    etat,
    depotId,
    travailId: `travail-demo-${String((compteur += 1))}`,
    natures: ['images', 'documents'],
    fichiers: 0,
    octets: 0,
    dejaPresents: 0,
    ecartes: 0,
    enregistrements: 0,
    lanceLe: new Date().toISOString(),
    termineLe: null,
    raison: null,
    ...sur,
  };
}

function ouvrirDepot(galerieId: string, plafondOctets: number, chemin: string): string {
  const depot = demoDepots.creer(galerieId, {
    libelle: `Dropbox — ${chemin || 'tout le compte'}`,
    plafondOctets,
  });
  return depot.id;
}

/** Les compteurs d'un import terminé : tout est versé, le dépôt est inventorié. */
function conclure(galerieId: string, imp: ImportConnexion): void {
  demoDepots.verser(galerieId, imp.depotId, FICHIERS);
  imp.etat = 'termine';
  imp.fichiers = FICHIERS.length;
  imp.octets = OCTETS_TOTAUX;
  imp.dejaPresents = DEJA_PRESENTS;
  imp.ecartes = ECARTES;
  imp.enregistrements = FICHIERS.length;
  imp.termineLe = new Date().toISOString();
}

function amorcer(dossierId: string): EtatDemo {
  const { galerieId, rang } = dossierDe(dossierId);
  const connexion = connexionVierge();
  const etat: EtatDemo = { connexion, lancement: null };
  if (galerieId === null) return etat;
  const compte = compteDemoCourant().compte.email;
  const il_y_a = (ms: number): string => new Date(Date.now() - ms).toISOString();
  const autorisee = (): void => {
    connexion.etat = 'autorisee';
    connexion.compte = COMPTE;
    connexion.autoriseeLe = il_y_a(26 * MINUTE);
    connexion.autoriseePar = compte;
    connexion.source = { chemin: '/Galerie' };
    connexion.revision = 4;
  };
  if (rang === 4) {
    connexion.etat = 'en_attente';
    connexion.autorisationExpireLe = new Date(Date.now() + 11 * MINUTE).toISOString();
    connexion.revision = 1;
  } else if (rang === 5) {
    autorisee();
    const depotId = ouvrirDepot(galerieId, 50 * GIO, '/Galerie');
    connexion.import = importDe('en_cours', depotId, { lanceLe: il_y_a(9_000) });
    etat.lancement = { debut: Date.now() - 9_000, duree: DUREE_IMPORT_MS, depotId, fini: false };
  } else if (rang === 6) {
    autorisee();
    connexion.autoriseeLe = il_y_a(3 * 24 * 60 * MINUTE);
    const depotId = ouvrirDepot(galerieId, 50 * GIO, '/Galerie');
    connexion.import = importDe('termine', depotId, { lanceLe: il_y_a(3 * 24 * 60 * MINUTE) });
    conclure(galerieId, connexion.import);
    connexion.import.termineLe = il_y_a(3 * 24 * 60 * MINUTE - 7 * MINUTE);
  } else if (rang === 7) {
    connexion.etat = 'revoquee';
    connexion.compte = COMPTE;
    connexion.autoriseeLe = il_y_a(20 * 24 * 60 * MINUTE);
    connexion.autoriseePar = compte;
    connexion.source = { chemin: '/Galerie' };
    connexion.revoqueLe = il_y_a(13 * 24 * 60 * MINUTE);
    connexion.revision = 7;
    const depotId = ouvrirDepot(galerieId, 50 * GIO, '/Galerie');
    connexion.import = importDe('termine', depotId, { lanceLe: il_y_a(19 * 24 * 60 * MINUTE) });
    conclure(galerieId, connexion.import);
  }
  return etat;
}

function etatDe(dossierId: string): EtatDemo {
  const connu = etats.get(dossierId);
  if (connu) return connu;
  const neuf = amorcer(dossierId);
  etats.set(dossierId, neuf);
  return neuf;
}

/** Fait avancer l'import avec le temps, et rend la vue à jour. */
function vue(dossierId: string): ConnexionDropbox {
  const etat = etatDe(dossierId);
  const { connexion, lancement } = etat;
  const { galerieId } = dossierDe(dossierId);
  const imp = connexion.import;
  if (imp && lancement && !lancement.fini && galerieId !== null) {
    const part = Math.min(1, (Date.now() - lancement.debut) / lancement.duree);
    if (part >= 1) {
      conclure(galerieId, imp);
      lancement.fini = true;
    } else {
      imp.etat = 'en_cours';
      imp.fichiers = Math.round(FICHIERS.length * part * 0.96);
      imp.octets = Math.round(OCTETS_TOTAUX * part);
      imp.dejaPresents = Math.round(DEJA_PRESENTS * part);
      imp.ecartes = part > 0.5 ? ECARTES : 0;
      imp.enregistrements = imp.fichiers;
    }
  }
  const copie = structuredClone(connexion);
  if (absent()) {
    copie.disponible = false;
    copie.mode = null;
  }
  return copie;
}

function exigerGalerie(dossierId: string): string {
  const { galerieId } = dossierDe(dossierId);
  if (galerieId === null)
    throw refus(
      409,
      'conflit.version',
      'galerie_absente',
      'Créez d’abord la galerie de ce dossier : le connecteur dépose dans son stockage.',
    );
  return galerieId;
}

function exigerDisponible(): void {
  if (absent())
    throw refus(
      503,
      'interne.inattendue',
      'connecteur_indisponible',
      'Le connecteur Dropbox n’est pas configuré sur cette plateforme (clé d’application absente).',
    );
}

function exigerAutorisee(dossierId: string): EtatDemo {
  const etat = etatDe(dossierId);
  if (etat.connexion.etat !== 'autorisee')
    throw refus(
      409,
      'conflit.version',
      'connexion_non_autorisee',
      'Autorisez d’abord l’accès à Dropbox.',
    );
  return etat;
}

export const demoConnecteurs = {
  lire(dossierId: string): { connexion: ConnexionDropbox } {
    return { connexion: vue(dossierId) };
  },

  autoriser(dossierId: string): ReponseAutorisation {
    exigerGalerie(dossierId);
    exigerDisponible();
    const { connexion } = etatDe(dossierId);
    if (connexion.etat === 'autorisee')
      throw refus(
        409,
        'conflit.version',
        'deja_autorisee',
        'Dropbox est déjà autorisé : révoquez d’abord.',
      );
    const expire = new Date(Date.now() + 15 * MINUTE).toISOString();
    connexion.etat = 'en_attente';
    connexion.autorisationExpireLe = expire;
    connexion.revision += 1;
    return {
      url: 'https://www.dropbox.com/oauth2/authorize?client_id=simulation&response_type=code&token_access_type=offline',
      expireLe: expire,
      connexion: vue(dossierId),
    };
  },

  coller(dossierId: string, code: string): { connexion: ConnexionDropbox } {
    exigerDisponible();
    const etat = etatDe(dossierId);
    const { connexion } = etat;
    const expiree =
      connexion.autorisationExpireLe !== null &&
      new Date(connexion.autorisationExpireLe).getTime() <= Date.now();
    if (connexion.etat !== 'en_attente' || expiree)
      throw refus(
        409,
        'conflit.version',
        'autorisation_expiree',
        'Aucune autorisation en cours, ou elle a expiré : demandez une nouvelle URL.',
      );
    if (code.trim().toLowerCase() === 'refuse')
      throw refus(
        400,
        'validation.invalide',
        'code_refuse',
        'Dropbox refuse ce code : vérifiez-le, ou demandez une nouvelle URL.',
      );
    connexion.etat = 'autorisee';
    connexion.compte = COMPTE;
    connexion.autoriseeLe = new Date().toISOString();
    connexion.autoriseePar = compteDemoCourant().compte.email;
    connexion.autorisationExpireLe = null;
    connexion.revoqueLe = null;
    connexion.revision += 1;
    return { connexion: vue(dossierId) };
  },

  dossiers(dossierId: string): ListeDossiersDropbox {
    exigerAutorisee(dossierId);
    return { dossiers: structuredClone(DOSSIERS), tronque: false };
  },

  choisir(dossierId: string, chemin: string): { connexion: ConnexionDropbox } {
    const etat = exigerAutorisee(dossierId);
    if (etat.connexion.import && ['en_attente', 'en_cours'].includes(vue(dossierId).import!.etat))
      throw refus(
        409,
        'conflit.version',
        'import_en_cours',
        'Un import tourne : attendez sa fin pour changer de dossier.',
      );
    if (chemin !== '' && !DOSSIERS.some((d) => d.chemin.toLowerCase() === chemin.toLowerCase()))
      throw refus(
        400,
        'validation.invalide',
        'dossier_inconnu',
        'Ce dossier n’est pas dans la liste de Dropbox.',
      );
    etat.connexion.source = { chemin };
    etat.connexion.revision += 1;
    return { connexion: vue(dossierId) };
  },

  lancer(dossierId: string, corps: LancementImport): ReponseLancement {
    const galerieId = exigerGalerie(dossierId);
    const etat = exigerAutorisee(dossierId);
    const { connexion } = etat;
    if (connexion.source === null)
      throw refus(
        409,
        'conflit.version',
        'source_absente',
        'Choisissez d’abord le dossier Dropbox à importer.',
      );
    const courant = vue(dossierId).import;
    if (courant && (courant.etat === 'en_attente' || courant.etat === 'en_cours'))
      throw refus(
        409,
        'conflit.version',
        'import_en_cours',
        'Un import tourne déjà pour ce dossier.',
      );
    const depotId = ouvrirDepot(
      galerieId,
      corps.plafondOctets ?? 50 * GIO,
      connexion.source.chemin,
    );
    connexion.import = importDe('en_attente', depotId, { natures: corps.natures });
    connexion.revision += 1;
    etat.lancement = { debut: Date.now() + 2_000, duree: DUREE_IMPORT_MS, depotId, fini: false };
    return {
      depotId,
      travailId: connexion.import.travailId,
      reprise: false,
      connexion: vue(dossierId),
    };
  },

  revoquer(dossierId: string): ReponseRevocation {
    const etat = etatDe(dossierId);
    const { connexion } = etat;
    if (connexion.etat === 'aucune')
      throw new ApiError(404, 'ressource.introuvable', 'Aucune connexion Dropbox pour ce dossier.');
    if (connexion.etat === 'revoquee' || connexion.etat === 'purgee')
      return { revocationDistante: 'sans_objet', connexion: vue(dossierId) };
    connexion.etat = 'revoquee';
    connexion.autorisationExpireLe = null;
    connexion.revoqueLe = new Date().toISOString();
    connexion.revision += 1;
    // Un import qui tourne est arrêté : son travail ne trouve plus de jeton.
    if (connexion.import && ['en_attente', 'en_cours'].includes(connexion.import.etat)) {
      connexion.import.etat = 'abandonne';
      connexion.import.raison = 'connexion_revoquee';
      connexion.import.termineLe = new Date().toISOString();
      if (etat.lancement) etat.lancement.fini = true;
    }
    return {
      revocationDistante: variante() === 'revocation-echec' ? 'echec' : 'faite',
      connexion: vue(dossierId),
    };
  },
};
